// Окно «Передать роль» (T048; contracts/ui.md, «Действия и подтверждения»; FR-018 – FR-018б):
// роль, заместитель, с и по. Администратор клиента выбирает, чья роль, и передаёт любую;
// владелец роли передаёт свои. Роль «сопровождение» и роль, полученная по замещению,
// не передаются — их нет в списке. Заместитель — из кандидатов GET /api/substitutions/candidates
// с поиском по ФИО. Проверка при отправке, ошибка под полем, фокус — в первое поле с ошибкой
// (FR-030). Пересечение (REC-005) — причиной и шагом от сервера и самим пересечением:
// заместитель, с и по (граничный случай «Два замещения пересекаются»); запрет (REC-006) —
// причиной и шагом от сервера. Отказ сервера по полям — текст под полем, код и номер операции —
// в выноске над полями (T058). После передачи — тост «Роль {роль} передана до {дата}».
// «Роль» не открывает пустое меню (T135, T136): пока роли грузятся дольше 0,3 с — «Загрузка…»
// и в меню строка «Загружаем роли…»; быстрее 0,3 с загрузка не мигает (T152, ux.md, раздел 14);
// не загрузились — в меню строка с кодом и «Повторить», под полем выноска с причиной, номером
// операции и «Повторить»; передать нечего — поле неактивно с причиной. Строки меню — сообщения
// DropdownMessage, а не неактивные варианты: текст читаем (README «Доступность», T150).
// «С» и «По» — поле даты «дд.мм.гггг» (T145; README «Тексты»): неполная дата («20.1») проверяется
// при отправке, как пустая — «Введите дату, например 20.10.2026»; введённое не стирается, ошибка
// уходит, как только дата стала верной (ux.md, раздел 13).
import {
  CreateSubstitutionBodySchema,
  fieldErrorsFromIssues,
  fieldMessages,
  nonTransferableRoles,
  roleInfo,
} from '@kp/contracts';
import type { ApiError, Assignment, Permissions, Role } from '@kp/contracts';
import {
  Button,
  Callout,
  DateField,
  DropdownMessage,
  Modal,
  ModalFooter,
  PickerField,
  useDelayedFlag,
  useToast,
} from '@kp/ui';
import type { PickerOption, SearchPickerOption } from '@kp/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { api, apiQuery, isApiError } from '../../api/client.ts';
import { FieldsRefusalCallout } from '../../app/FieldsRefusal.tsx';
import { errorView, hasFieldTexts, useErrorToast } from '../../app/errors.ts';
import type { ErrorView, FieldsRefusal } from '../../app/errors.ts';
import { formatDate, todayIn } from '../../app/format.ts';
import { areaLabel } from '../../app/labels.ts';
import type { CreateModalProps } from '../../shell/context.tsx';
import { meQueryKey, useMe, useTimeZone } from '../../shell/session.ts';
import { ListLoadError, useQueryFailure } from '../load-error.tsx';
import { UserSearchField } from '../users/UserSearchField.tsx';
import { DeputySearchField } from './DeputySearchField.tsx';

const ROLES_FAILED = 'Не удалось загрузить роли';

type FieldName = 'owner' | 'assignment' | 'deputy' | 'validFrom' | 'validTo';
type DateName = 'validFrom' | 'validTo';
const FIELD_ORDER: readonly FieldName[] = ['owner', 'assignment', 'deputy', 'validFrom', 'validTo'];

const serverFields: Readonly<Record<string, FieldName>> = {
  assignmentId: 'assignment',
  deputyUserId: 'deputy',
  validFrom: 'validFrom',
  validTo: 'validTo',
};

function mapFields(fields: Readonly<Record<string, string>>): Partial<Record<FieldName, string>> {
  const result: Partial<Record<FieldName, string>> = {};
  for (const [key, text] of Object.entries(fields)) {
    const field = serverFields[key];
    if (field && !result[field]) result[field] = text;
  }
  return result;
}

/**
 * Назначение можно передать: своё, не в архиве, не «сопровождение» и не замещение (FR-018а).
 * Своё назначение действует без срока (FR-014а): сверять с ним даты замещения не нужно.
 */
export function transferable(assignment: Assignment): boolean {
  return (
    assignment.archivedAt === undefined &&
    assignment.substitutesAssignmentId === undefined &&
    !(nonTransferableRoles as readonly Role[]).includes(assignment.role)
  );
}

/** Пересечение REC-005: «Пересекается: Рябов Р. Р., 20.10.2026 — 31.10.2026». */
export function overlapText(overlap: NonNullable<ApiError['overlap']>): string {
  return `Пересекается: ${overlap.deputy.fullName}, ${formatDate(overlap.validFrom)} — ${formatDate(overlap.validTo)}`;
}

/**
 * Чью роль передавать — выбирает администратор клиента: он передаёт любую роль и видит список
 * пользователей. Остальные передают свои роли.
 */
export function picksOwner(permissions: Permissions): boolean {
  return permissions['substitutions.create'] === 'all' && permissions['users.read'] !== undefined;
}

function assignmentOption(assignment: Assignment): PickerOption<string> {
  return {
    value: assignment.id,
    label: roleInfo[assignment.role].title,
    description: assignment.areas.map(areaLabel).join(', '),
  };
}

/** Отказ сервера в окне: причина, шаг, код и пересечение, если оно есть. */
interface Refusal extends ErrorView {
  overlap?: ApiError['overlap'];
}

export function TransferRoleModal({ open, onOpenChange, returnFocusRef }: CreateModalProps) {
  const toast = useToast();
  const showError = useErrorToast();
  const queryClient = useQueryClient();
  const me = useMe();
  const timeZone = useTimeZone();
  const today = todayIn(timeZone);
  const pickOwner = picksOwner(me.permissions);

  const [owner, setOwner] = useState<SearchPickerOption | null>(null);
  const [assignmentId, setAssignmentId] = useState<string | null>(null);
  const [deputy, setDeputy] = useState<SearchPickerOption | null>(null);
  const [validFrom, setValidFrom] = useState(today);
  const [validTo, setValidTo] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  /** Отказ сервера по полям: код и номер операции — в выноске над полями (T058). */
  const [fieldsRefusal, setFieldsRefusal] = useState<FieldsRefusal<FieldName> | null>(null);
  const [pending, setPending] = useState(false);

  const ownerRef = useRef<HTMLButtonElement>(null);
  const assignmentRef = useRef<HTMLButtonElement>(null);
  const deputyRef = useRef<HTMLButtonElement>(null);
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);

  // Чья роль: администратор выбирает, владелец передаёт свою.
  const ownerId = pickOwner ? owner?.value : me.user.id;
  const assignments = useQuery({
    ...apiQuery('assignments.listForUser', { params: { id: ownerId ?? '' } }),
    enabled: ownerId !== undefined,
  });
  const options = (assignments.data ?? []).filter(transferable).map(assignmentOption);
  const chosen = assignments.data?.find((item) => item.id === assignmentId);
  const rolesPending = ownerId !== undefined && assignments.isPending;
  // «Загрузка…» — только если ответ дольше 0,3 с (FR-040а); фокус остаётся в поле.
  const rolesLoading = useDelayedFlag(rolesPending);
  // Сбой — выноска под полем; на время «Повторить» она на месте со спиннером (T168).
  const {
    failure: rolesFailure,
    retrying: rolesRetrying,
    retry: retryRoles,
  } = useQueryFailure(assignments);
  // Пока ответа нет — в меню ни ролей, ни строки; дольше 0,3 с — «Загружаем роли…» (задержку
  // ведёт сама строка). Сбой — строка с кодом и «Повторить»: другого пункта в меню нет; повтор
  // идёт — снова загрузка.
  let rolesMessage: ReactNode = null;
  if (rolesFailure && !rolesRetrying) {
    rolesMessage = (
      <DropdownMessage tone="error" code={rolesFailure.code} onRetry={retryRoles}>
        {ROLES_FAILED}
      </DropdownMessage>
    );
  } else if (rolesPending) {
    rolesMessage = <DropdownMessage tone="loading">Загружаем роли…</DropdownMessage>;
  }

  const clearError = (field: FieldName) => {
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const showErrors = (found: Partial<Record<FieldName, string>>) => {
    setErrors(found);
    const first = FIELD_ORDER.find((field) => found[field]);
    const target = {
      owner: ownerRef.current,
      assignment: assignmentRef.current,
      deputy: deputyRef.current,
      validFrom: fromRef.current,
      validTo: toRef.current,
    };
    if (first) target[first]?.focus();
  };

  const validate = (dates: Partial<Record<DateName, string>> = {}) => {
    const parsed = CreateSubstitutionBodySchema.safeParse({
      assignmentId: assignmentId ?? '',
      deputyUserId: deputy?.value ?? '',
      validFrom,
      validTo,
      ...dates,
    });
    const found: Partial<Record<FieldName, string>> = parsed.success
      ? {}
      : mapFields(fieldErrorsFromIssues(parsed.error.issues));
    if (pickOwner && !owner) found.owner = fieldMessages.user;
    return found;
  };

  /**
   * Дата изменилась: показанная ошибка даты остаётся, пока дата не стала верной, и меняется, если
   * стала другой («Введите дату, например…» → «Введите дату не раньше «С»…»).
   */
  const changeDate = (field: DateName, value: string) => {
    const dates = { validFrom, validTo, [field]: value };
    if (field === 'validFrom') setValidFrom(value);
    else setValidTo(value);
    if (!errors.validFrom && !errors.validTo) return;
    const found = validate(dates);
    setErrors((current) => ({
      ...current,
      validFrom: current.validFrom ? found.validFrom : undefined,
      validTo: current.validTo ? found.validTo : undefined,
    }));
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending) return;
    setRefusal(null);
    setFieldsRefusal(null);
    const found = validate();
    if (Object.keys(found).length > 0 || !assignmentId || !deputy || !chosen) {
      showErrors(found);
      return;
    }
    setPending(true);
    try {
      await api('substitutions.create', {
        body: { assignmentId, deputyUserId: deputy.value, validFrom, validTo },
      });
    } catch (error) {
      setPending(false);
      const fields = isApiError(error, 'REC-007') ? mapFields(error.fields) : {};
      if (hasFieldTexts(fields)) {
        showErrors(fields);
        setFieldsRefusal({ fields, view: errorView(error) });
      } else if (isApiError(error, 'REC-005') || isApiError(error, 'REC-006')) {
        setRefusal({
          message: error.message,
          nextStep: error.nextStep,
          code: error.code,
          operation: error.operation,
          overlap: error.body.overlap,
        });
      } else {
        showError(error, () => void submit());
      }
      return;
    }
    setPending(false);
    void queryClient.invalidateQueries({ queryKey: ['api', 'substitutions.list'] });
    void queryClient.invalidateQueries({ queryKey: ['api', 'users.get'] });
    void queryClient.invalidateQueries({ queryKey: meQueryKey });
    toast.ok(`Роль ${roleInfo[chosen.role].inText} передана до ${formatDate(validTo)}`);
    onOpenChange(false);
  };

  const formId = useId();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Передать роль"
      description="Права роли перейдут заместителю на срок, у владельца роли они останутся"
      returnFocusRef={returnFocusRef}
      onSubmitShortcut={() => void submit()}
      footer={
        <ModalFooter
          submitHint
          primary={
            <Button type="submit" form={formId} variant="primary" loading={pending}>
              Передать роль
            </Button>
          }
        />
      }
    >
      <form id={formId} className="t-col" noValidate onSubmit={(event) => void submit(event)}>
        {refusal ? (
          <Callout
            tone="bad"
            role="alert"
            lead={`${refusal.message}.`}
            code={refusal.code}
            operation={refusal.operation}
          >
            {refusal.overlap ? <>{overlapText(refusal.overlap)}. </> : null}
            {refusal.nextStep}
          </Callout>
        ) : null}
        <FieldsRefusalCallout refusal={fieldsRefusal} errors={errors} />
        {pickOwner ? (
          <UserSearchField
            ref={ownerRef}
            label="Чья роль"
            required
            autoFocus
            value={owner}
            error={errors.owner}
            onChange={(option) => {
              setOwner(option);
              setAssignmentId(null);
              if (deputy?.value === option.value) setDeputy(null);
              clearError('owner');
            }}
          />
        ) : null}
        <PickerField<string>
          ref={assignmentRef}
          label="Роль"
          required
          autoFocus={!pickOwner}
          placeholder={rolesLoading ? 'Загрузка…' : 'Выберите роль'}
          value={assignmentId}
          options={options}
          message={rolesMessage}
          onChange={(value) => {
            setAssignmentId(value);
            clearError('assignment');
          }}
          disabled={ownerId === undefined || (assignments.isSuccess && options.length === 0)}
          disabledReason={
            ownerId === undefined
              ? 'Сначала выберите, чья роль'
              : 'Передать нечего: роль «сопровождение» и роль по замещению не передаются'
          }
          hint={
            chosen
              ? roleInfo[chosen.role].description
              : rolesLoading
                ? 'Загружаем роли пользователя'
                : undefined
          }
          error={errors.assignment}
        />
        {rolesFailure ? (
          <ListLoadError
            what={ROLES_FAILED}
            view={rolesFailure}
            retrying={rolesRetrying}
            onRetry={retryRoles}
          />
        ) : null}
        <DeputySearchField
          ref={deputyRef}
          required
          value={deputy}
          excludeId={ownerId}
          error={errors.deputy}
          onChange={(option) => {
            setDeputy(option);
            clearError('deputy');
          }}
        />
        <div className="kp-form-row">
          <DateField
            ref={fromRef}
            label="С"
            required
            min={today}
            today={today}
            value={validFrom}
            error={errors.validFrom}
            onChange={(value) => changeDate('validFrom', value)}
          />
          <DateField
            ref={toRef}
            label="По"
            required
            min={validFrom || today}
            today={today}
            value={validTo}
            hint="Последний день замещения"
            error={errors.validTo}
            onChange={(value) => changeDate('validTo', value)}
          />
        </div>
      </form>
    </Modal>
  );
}
